import currency from "currency.js";
import moment from "moment";
import { IConfirmationModalStat } from ".";
import {
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { apiNum } from "../../utils/math";
import { signedTone, UNKNOWN_DISPLAY } from "../../utils/unknownNumbers";
import { slippagePercentText } from "../../utils/amount/slippage";
import { nanosToSeconds } from "../../utils/date";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../utils/format";
import { IMarketPrecisionGetter } from "../../contexts/MarketInstrumentContext";
import { PositionCloseEstimate } from "../../utils/positionClose";
import { ClosePositionPageEnum } from "./ClosePositionModal.types";

type TranslateFn = (...args: any[]) => any;

interface IBuildCloseStatsParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  page: ClosePositionPageEnum;
  isPerp: boolean;
  positionAmount: number;
  closeSizeInput: string;
  closeEstimate: PositionCloseEstimate | undefined;
  getMarketPrecision: IMarketPrecisionGetter;
  /** Set for a MARKET close: the bound in force and the price it implies. */
  slippage?: { bps: number; worstFillText?: string };
  t: TranslateFn;
}

export const buildCloseStats = ({
  position,
  page,
  isPerp,
  positionAmount,
  closeSizeInput,
  closeEstimate,
  getMarketPrecision,
  slippage,
  t,
}: IBuildCloseStatsParams): IConfirmationModalStat[] => {
  if (!position || page !== ClosePositionPageEnum.DETAILS) {
    return [];
  }

  // Scale displayed PnL/value by the fraction being closed so partial closes
  // show the PnL for the size selected, not the whole position. ROI is
  // fraction-independent (numerator and denominator both scale).
  const closeFrac =
    isPerp && positionAmount > 0
      ? Math.min(1, (Number(closeSizeInput) || 0) / positionAmount)
      : 1;
  const fullValue =
    Number(position.amount) * Number(position.avg_entry_price);
  // "Est. Close PnL" must be what CLOSING realises, not the whole-position
  // outlook. `closeEstimate.pnl` adds the unrealised PnL of the part the book
  // cannot absorb, which on a thin book outweighs the realised loss and printed
  // a green profit for a close that loses money. `realizedPnl` is the close
  // alone; `pnl` remains available for a whole-position figure.
  //
  // Unknown stays unknown: `Number(null)` showed "+$0.00" / "+0.00%" for a
  // position whose market (and so PnL) could not be resolved.
  const basePnl = closeEstimate?.realizedPnl ?? apiNum(position.unrealized_pnl);
  const displayPnl = basePnl === undefined ? undefined : basePnl * closeFrac;
  // ROI shares the numerator's basis: a close-only PnL is measured against the
  // entry notional of the portion that closes, not of the whole position, or a
  // partial close understates its own return.
  const roiBase = closeEstimate?.closedEntryValue ?? fullValue;
  const roi =
    basePnl !== undefined && roiBase !== 0
      ? (basePnl / roiBase) * 100
      : undefined;
  const pendingFundingFee = Number(
    position.pending_close_funding_fee || position.pending_funding_fee || 0
  );
  const fundingFeeDisplayValue = -pendingFundingFee;
  const pricePrecision = getMarketPrecision(
    position.asset,
    position.instrument_type
  ).price_precision;

  const items = [
    {
      title: t("contracts"),
      data: formatSizePrecision(
        isPerp ? closeSizeInput || position.amount : position.amount
      ),
    },
    {
      title: t("avg_entry_price"),
      data: currency(position.avg_entry_price || 0, {
        precision: pricePrecision,
      }).format(),
    },
    {
      title: t("estimated_exit_price", {
        defaultValue: "Est. Exit Price",
      }),
      data:
        closeEstimate?.averagePrice !== undefined
          ? currency(closeEstimate.averagePrice, {
              precision: pricePrecision,
            }).format()
          : "-",
    },
    {
      title:
        closeEstimate?.realizedPnl !== undefined
          ? t("estimated_close_pnl", { defaultValue: "Est. Close PnL" })
          : t("unrealized_pnl"),
      data: (
        <span
          style={{
            color: signedTone(displayPnl, {
              up: COLORS.up.one,
              down: COLORS.down.one,
              unknown: TEXT_COLORS.three,
            }),
          }}
        >
          {formatCurrencyWithSubscript(displayPnl, {
            precision: 2,
            showPositiveSign: true,
          })}
        </span>
      ),
    },
    {
      title: t("roi"),
      data: (
        <span
          style={{
            color: signedTone(roi, {
              up: COLORS.up.one,
              down: COLORS.down.one,
              unknown: TEXT_COLORS.three,
            }),
          }}
        >
          {roi === undefined ? "-" : `${roi < 0 ? "" : "+"}${roi.toFixed(2)}%`}
        </span>
      ),
    },
  ];

  // A market close is bounded, and this is the screen where the trader commits
  // to it. The bound existed before but appeared nowhere: the only way to learn
  // the price it implied was to be refused by the chain.
  if (slippage) {
    items.push({
      title: t("max_slippage", { defaultValue: "Max slippage" }),
      data: `${slippagePercentText(slippage.bps)}%`,
    });
    items.push({
      title: t("worst_fill", { defaultValue: "Fills no worse than" }),
      data:
        slippage.worstFillText === undefined
          ? UNKNOWN_DISPLAY
          : currency(slippage.worstFillText, { precision: pricePrecision }).format(),
    });
  }

  if (pendingFundingFee !== 0) {
    items.push({
      title: t("funding_fee"),
      data: (
        <span
          style={{
            color:
              fundingFeeDisplayValue >= 0
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {formatCurrencyWithSubscript(fundingFeeDisplayValue, {
            showPositiveSign: true,
          })}
        </span>
      ),
    });
  }

  if (position.side === SideResponse.Sell) {
    items.push({
      title: t("maintenance_margin"),
      data: currency(position.maintenance_margin).format(),
    });
  }

  if (position.option) {
    items.push(
      ...[
        {
          title: t("delta"),
          data: Number(position.option.delta).toFixed(2) || "-",
        },
        {
          title: t("theta"),
          data: currency(position.option.theta).format() || "-",
        },
        {
          title: t("vega"),
          data: Number(position.option.vega).toFixed(2) || "-",
        },
        {
          title: t("gamma"),
          data: Number(position.option.gamma).toFixed(6) || "-",
        },
      ]
    );
  }

  return items;
};

interface IBuildTagsParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  page: ClosePositionPageEnum;
  t: TranslateFn;
}

export const buildTags = ({ position, page, t }: IBuildTagsParams) => {
  if (position && page === ClosePositionPageEnum.DETAILS) {
    return {
      texts: position.option
        ? [
            position.side === SideResponse.Buy ? t("long") : t("short"),
            position.option.option_type,
            moment
              .unix(nanosToSeconds(position.option.expiry))
              .format("DD MMM YY"),
            currency(position.option.strike, { precision: 0 }).format(),
          ]
        : [
            position.side === SideResponse.Buy ? t("long") : t("short"),
            position.instrument_name,
          ],
      color:
        position.side === SideResponse.Buy
          ? COLORS.up.one
          : COLORS.down.one,
      backgroundColor:
        position.side === SideResponse.Buy
          ? COLORS.up.four
          : COLORS.down.four,
    };
  }
  return undefined;
};
